import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { TaskStatusBadge, PriorityBadge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Input';
import {
  X, CheckCircle, AlertCircle, MessageSquare, Send,
  Download, Calendar, User, FileText, Settings2, Trash2,
} from 'lucide-react';
import type { Developer, Task, TaskStatus } from '@/types';
import { formatDate, formatDateRelative } from '@/lib/utils';

interface TaskDetailModalProps {
  task: Task;
  onClose: () => void;
  onAction: (taskId: string, action: string, data?: any) => void;
  isAdmin?: boolean;
  developers?: Developer[];
}

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'queued', label: 'Queued' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'approved', label: 'Approved' },
];

export function TaskDetailModal({ task, onClose, onAction, isAdmin = false, developers = [] }: TaskDetailModalProps) {
  const [comment, setComment] = useState('');
  const [isRevision, setIsRevision] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus>(task.status);
  const [selectedDevId, setSelectedDevId] = useState<string>(task.assignedDeveloper?.id ?? '');
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDescription, setEditDescription] = useState(task.description);
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editDueDate, setEditDueDate] = useState(task.estimatedCompletion.slice(0, 10));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    onAction(task.id, 'comment', { content: comment, author: isAdmin ? 'OrbitCrew' : 'Client', isRevision });
    setComment('');
    setIsRevision(false);
  };

  const handleApprove = () => onAction(task.id, 'approve', { author: isAdmin ? 'Admin' : 'Client' });

  const handleRevision = () => {
    if (!comment.trim()) return;
    onAction(task.id, 'revision', { content: comment, author: isAdmin ? 'OrbitCrew' : 'Client' });
    setComment('');
  };

  const handleStatusUpdate = () => {
    if (selectedStatus !== task.status) {
      onAction(task.id, 'updateStatus', { status: selectedStatus });
    }
  };

  const handleAssignDeveloper = () => {
    onAction(task.id, 'assignDeveloper', {
      developerId: selectedDevId === '' ? null : selectedDevId,
    });
  };

  const handleEditSave = () => {
    onAction(task.id, 'editTask', {
      title: editTitle,
      description: editDescription,
      priority: editPriority,
      estimatedCompletion: new Date(editDueDate).toISOString(),
    });
    setIsEditingTask(false);
  };

  const handleDelete = () => {
    if (confirmDelete) {
      onAction(task.id, 'deleteTask');
      onClose();
    } else {
      setConfirmDelete(true);
    }
  };

  const canApprove = task.status === 'delivered' || task.status === 'review';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-100">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <TaskStatusBadge status={task.status} />
              <PriorityBadge priority={task.priority} />
              <span className="text-xs text-slate-400">#{task.id}</span>
            </div>
            <h2 className="text-xl font-bold text-slate-900 truncate">{task.title}</h2>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isAdmin && (
              <button
                onClick={() => { setIsEditingTask(!isEditingTask); setConfirmDelete(false); }}
                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit task"
              >
                <Settings2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Edit Task Form (admin only) */}
          {isAdmin && isEditingTask && (
            <div className="p-6 border-b border-slate-100 bg-slate-50 space-y-3">
              <h3 className="font-semibold text-slate-900 text-sm">Edit Task</h3>
              <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
              />
              <textarea
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Description"
              />
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">Priority</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editPriority}
                    onChange={(e) => setEditPriority(e.target.value as 'standard' | 'urgent')}
                  >
                    <option value="standard">Standard</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">Due Date</label>
                  <input
                    type="date"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleEditSave} disabled={!editTitle.trim() || !editDescription.trim()}>
                  Save Changes
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditingTask(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Admin Controls */}
          {isAdmin && !isEditingTask && (
            <div className="p-6 border-b border-slate-100 bg-blue-50 space-y-3">
              <h3 className="font-semibold text-slate-700 text-sm">Admin Controls</h3>
              <div className="flex gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <select
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as TaskStatus)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <Button size="sm" onClick={handleStatusUpdate} disabled={selectedStatus === task.status}>
                    Update Status
                  </Button>
                </div>
                {developers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <select
                      className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedDevId}
                      onChange={(e) => setSelectedDevId(e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {developers.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" onClick={handleAssignDeveloper}>
                      Assign
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-6">
            {/* Meta info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Due Date</span>
                </div>
                <p className="font-medium text-slate-900">{formatDate(task.estimatedCompletion)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                  <User className="w-4 h-4" />
                  <span>Assigned To</span>
                </div>
                {task.assignedDeveloper ? (
                  <div className="flex items-center gap-2">
                    <Avatar src={task.assignedDeveloper.avatar} name={task.assignedDeveloper.name} size="xs" />
                    <span className="font-medium text-slate-900 text-sm">{task.assignedDeveloper.name}</span>
                  </div>
                ) : (
                  <span className="text-slate-500 text-sm">Unassigned</span>
                )}
              </div>
            </div>

            {/* Files */}
            {task.files.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4" />
                  Attachments ({task.files.length})
                </h3>
                <div className="space-y-2">
                  {task.files.map((file) => (
                    <div key={file.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-9 h-9 bg-white rounded border border-slate-200 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{file.name}</p>
                        <p className="text-xs text-slate-500">{file.size} · {file.uploadedBy}</p>
                      </div>
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors shrink-0"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
                <MessageSquare className="w-4 h-4" />
                Comments ({task.comments.length})
              </h3>

              {task.comments.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">No comments yet</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {task.comments.map((c) => (
                    <div key={c.id} className={`p-4 rounded-lg ${c.isRevision ? 'bg-amber-50 border border-amber-100' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Avatar name={c.author} size="xs" />
                        <span className="font-medium text-slate-900 text-sm">{c.author}</span>
                        <span className="text-xs text-slate-400">{formatDateRelative(c.createdAt)}</span>
                        {c.isRevision && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                            Revision Request
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700">{c.content}</p>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleSubmitComment} className="space-y-3">
                <Textarea
                  placeholder={isAdmin ? 'Add a comment as OrbitCrew...' : 'Add a comment or request a revision...'}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                />
                <div className="flex items-center justify-between">
                  {!isAdmin ? (
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isRevision}
                        onChange={(e) => setIsRevision(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      <AlertCircle className="w-4 h-4" />
                      This is a revision request
                    </label>
                  ) : (
                    <span />
                  )}
                  <Button type="submit" size="sm" leftIcon={<Send className="w-4 h-4" />} disabled={!comment.trim()}>
                    Send
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 pb-6 flex gap-3 flex-wrap">
            {canApprove && (
              <>
                <Button onClick={handleApprove} leftIcon={<CheckCircle className="w-4 h-4" />}>
                  Approve Delivery
                </Button>
                {!isAdmin && (
                  <Button
                    onClick={handleRevision}
                    variant="outline"
                    disabled={!comment.trim()}
                    leftIcon={<AlertCircle className="w-4 h-4" />}
                  >
                    Request Revision
                  </Button>
                )}
              </>
            )}
            {isAdmin && (
              <button
                onClick={handleDelete}
                className={`ml-auto flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg transition-colors ${
                  confirmDelete
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'text-red-500 hover:bg-red-50 border border-red-200'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                {confirmDelete ? 'Confirm Delete' : 'Delete Task'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
